<template>
    <div class="search-filters-display">
        <div v-if="activeFilters.length > 0" class="flex flex-wrap gap-1.5">
            <SearchFilterBadge v-for="filter in activeFilters" :key="filter.id" :filter="filter" />
        </div>
        <span v-else class="text-xs italic text-muted-foreground"> Default filters </span>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import SearchFilterBadge from './SearchFilterBadge.vue'

const props = defineProps({
    search: {
        type: Object,
        required: true,
    },
})

const filterGroups = {
    aggregation: ['day_range', 'content_type'],
    channel: [
        'country',
        'language',
        'gender',
        'age_range',
        'account_type',
        'email',
        'account_made_for_kids',
        'region',
    ],
    metrics: [
        'avg_viewers',
        'avg_views',
        'avg_likes',
        'sum_content_items',
        'avg_engagement_rate',
        'sponsorship_saturation',
        'followers',
    ],
    niche: [
        'classifications.hierarchy.topic',
        'classifications.hierarchy.category',
        'interests.include.values',
        'interests.exclude.values',
    ],
    games: [
        'games_list.include.values',
        'games_list.exclude.values',
        'games_list_matched_count.min',
        'games_count.min',
        'game_categories_list.include.values',
        'game_categories_list.exclude.values',
    ],
    content: [
        'account_rating_remapped',
        'has_sponsorship',
        'content_formats.include.values',
        'content_formats.exclude.values',
        'hashtags_list.include.values',
        'hashtags_list.exclude.values',
    ],
    audience: [
        'audience_countries.include.values',
        'audience_languages.include.values',
        'audience_gender_distribution.include.values',
        'audience_age_ranges.include.values',
        'audience_ethnicity_distribution.include.values',
        'audience_education_level.include.values',
        'audience_family_status.include.values',
        'audience_income_distribution.include.values',
        'audience_occupation_distribution.include.values',
        'audience_engagement_style.include.values',
        'audience_brand_safety',
        'audience_cohesion_score',
        'audience_toxicity_score',
    ],
    restrictions: ['alternate_platforms.include.values'],
}

// Filter out meaningless values from a filters array
function getActiveFilters(filters) {
    return filters
        .filter((filter) => {
            // Skip conjunction filters
            if (filter.id.includes('conjunction')) return false

            // For array values, only include if they have items
            if (Array.isArray(filter.value)) {
                return filter.value.length > 0
            }

            // For the minimum matched games filter, only show if greater than 0
            if (filter.id === 'min-matched-games') {
                return filter.value > 0
            }

            // For numeric values, include day_range and any positive numbers
            if (typeof filter.value === 'number') {
                return filter.id === 'day_range' || filter.value > 0
            }

            // For string values, include content-type
            if (typeof filter.value === 'string' && filter.id === 'content-type') {
                return true
            }

            // For other values, only include if they exist and are not empty
            return filter.value && filter.value !== ''
        })
        .map((filter) => ({
            id: filter.id,
            value: filter.value,
            category: getFilterCategory(filter.id),
        }))
        .sort((a, b) => getGroupOrder(a.category) - getGroupOrder(b.category))
}

// Convert query object into filter array
function queryToFilters(query) {
    if (!query || typeof query !== 'object') return []
    return Object.entries(query)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([id, value]) => ({ id, value }))
}

// Process the filters from the search object
const activeFilters = computed(() => {
    // Try paging.filters first
    const pagingFilters = props.search?.request?.body?.paging?.filters
    if (pagingFilters && pagingFilters.length > 0) {
        return getActiveFilters(pagingFilters)
    }

    // Fall back to body.query
    const queryFilters = props.search?.request?.body?.query
    if (queryFilters && Object.keys(queryFilters).length > 0) {
        return getActiveFilters(queryToFilters(queryFilters))
    }

    return []
})

// Determine which group a filter belongs to
function getFilterCategory(filterId) {
    for (const [group, filterIds] of Object.entries(filterGroups)) {
        if (filterIds.some((id) => filterId.includes(id) || filterId === id)) {
            return group
        }
    }
    return 'other'
}

// Define the order of groups for sorting
function getGroupOrder(group) {
    const groupOrder = {
        aggregation: 1,
        niche: 2,
        metrics: 3,
        channel: 4,
        content: 5,
        audience: 6,
        games: 7,
        restrictions: 8,
        other: 9,
    }
    return groupOrder[group] || 999
}
</script>
